// Unified Family page: babies, members, pending invites, and invite CTA.
// This is the single "Family" page in the app; /babies redirects here.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../api/client.ts';
import { useMe } from '../api/hooks.ts';
import { HeaderBar } from '../components/HeaderBar.tsx';
import { Icon } from '../components/Icon.tsx';
import { LionMark } from '../components/LionMark.tsx';
import { Section } from '../components/Section.tsx';
import { ShareLinkSheet } from '../components/sheets/ShareLinkSheet.tsx';
import { MemberEditSheet } from '../components/sheets/MemberEditSheet.tsx';
import { RELATIONSHIP_LABELS } from '@shared/schemas/family.ts';
import type { Baby, FamilyMember } from '@shared/types.ts';
import { ageLabel, relTime } from '../lib/format.ts';

type Invite = { id: string; expires_at: number; accepted_at: number | null; created_at: number };

const BABY_COLORS = ['#c96442', '#6b8e5a', '#7a8aa8', '#c97e96', '#d4a052', '#b09ac7'];

const prettyRelationship = (r: string | null | undefined): string | null =>
  r ? RELATIONSHIP_LABELS[r as keyof typeof RELATIONSHIP_LABELS] ?? 'Other' : null;

function isoToUtcMs(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  return Date.UTC(y, (m ?? 1) - 1, d ?? 1);
}
function toIsoDate(ms: number): string {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

export function Family() {
  const { data: me } = useMe();
  const qc = useQueryClient();
  const [inviteSheetOpen, setInviteSheetOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<FamilyMember | null>(null);
  const [addingBaby, setAddingBaby] = useState(false);
  const [editingBaby, setEditingBaby] = useState<Baby | null>(null);
  const meIsOwner = !!me?.members.find((m) => m.id === me?.user.id && m.role === 'owner');

  const activate = useMutation({
    mutationFn: (id: string) => api(`/api/babies/${id}/activate`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
    onError: (e) =>
      toast.error(e instanceof Error ? e.message : 'Could not switch active baby'),
  });

  const invitesQ = useQuery({
    queryKey: ['family', 'invites'],
    queryFn: () => api<{ invites: Invite[] }>('/api/family/invites'),
    select: (d) => d.invites,
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api(`/api/family/invites/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Invite revoked');
      qc.invalidateQueries({ queryKey: ['family', 'invites'] });
    },
  });

  return (
    <>
      <HeaderBar title="Family" showAvatar={false} />
      <div className="lt-scroll" style={{ flex: 1 }}>
        <Section title="Babies">
          {(me?.babies ?? []).map((b, i, arr) => (
            <BabyRow
              key={b.id}
              baby={b}
              active={b.id === me?.active_baby_id}
              onActivate={() => activate.mutate(b.id)}
              onEdit={() => setEditingBaby(b)}
              last={i === arr.length - 1}
            />
          ))}
        </Section>
        <div style={{ padding: '0 16px 18px' }}>
          <button
            type="button"
            className="lt-btn lt-btn-ghost"
            style={{ width: '100%', padding: '13px 0' }}
            onClick={() => setAddingBaby(true)}
          >
            <Icon.plus s={16} /> Add another baby
          </button>
        </div>

        <Section title="Members" hint="Tap a member to edit name, relationship, and height.">
          {(me?.members ?? []).map((u, i) => (
            <button
              type="button"
              key={u.id}
              onClick={() => setEditingMember(u)}
              style={{
                all: 'unset',
                boxSizing: 'border-box',
                width: '100%',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 16px',
                borderTop: i === 0 ? 'none' : '1px solid var(--divider)',
              }}
            >
              <div
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 999,
                  background: u.color ?? 'var(--accent)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'Newsreader, serif',
                  fontSize: 19,
                }}
              >
                {u.name[0]?.toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500 }}>
                  {u.name}
                  {u.id === me?.user.id && <span style={{ color: 'var(--text-3)' }}> (you)</span>}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>
                  {[
                    prettyRelationship(u.relationship),
                    u.height_mm != null ? `${(u.height_mm / 10).toFixed(0)} cm` : null,
                    u.last_seen_at ? `active ${relTime(u.last_seen_at)}` : 'never signed in',
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              </div>
              <Icon.chevR s={14} style={{ color: 'var(--text-3)' }} />
            </button>
          ))}
        </Section>

        <div style={{ padding: '0 16px 18px' }}>
          <button
            type="button"
            className="lt-btn lt-btn-primary"
            style={{ width: '100%', padding: '13px 0' }}
            onClick={() => setInviteSheetOpen(true)}
          >
            <Icon.share s={16} /> Invite via QR or share link
          </button>
        </div>

        {(invitesQ.data?.length ?? 0) > 0 && (
          <Section title="Pending invites">
            {invitesQ.data?.map((inv) => (
              <div
                key={inv.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 16px',
                }}
              >
                <div
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 999,
                    background: 'var(--bg-sunken)',
                    color: 'var(--text-3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon.user s={18} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500, fontSize: 14 }}>Open invite</div>
                  <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
                    created {relTime(inv.created_at)} · expires {relTime(inv.expires_at)}
                  </div>
                </div>
                <button
                  type="button"
                  className="lt-btn lt-btn-ghost"
                  style={{ padding: '6px 12px', fontSize: 12 }}
                  onClick={() => revoke.mutate(inv.id)}
                >
                  Revoke
                </button>
              </div>
            ))}
          </Section>
        )}
      </div>

      <MemberEditSheet
        open={!!editingMember}
        member={editingMember}
        isOwner={meIsOwner}
        isSelf={editingMember?.id === me?.user.id}
        onClose={() => setEditingMember(null)}
        onSaved={() => qc.invalidateQueries({ queryKey: ['me'] })}
      />

      <ShareLinkSheet
        open={inviteSheetOpen}
        onClose={() => {
          setInviteSheetOpen(false);
          qc.invalidateQueries({ queryKey: ['family', 'invites'] });
        }}
        title="Invite to the family"
        tagline="Scan with their phone, or share the link."
        shareTitle="Lion Tracker invite"
        shareText="Join me on Lion Tracker"
        generate={async () => {
          const r = await api<{ url: string; expires_at: number }>(
            '/api/family/invites',
            { method: 'POST', json: {} },
          );
          return r;
        }}
        caption="Single use · expires in 7 days"
      />

      {addingBaby && <BabyFormSheet onClose={() => setAddingBaby(false)} />}
      {editingBaby && (
        <BabyFormSheet existing={editingBaby} onClose={() => setEditingBaby(null)} />
      )}
    </>
  );
}

function BabyRow({
  baby,
  active,
  onActivate,
  onEdit,
  last,
}: {
  baby: Baby;
  active: boolean;
  onActivate: () => void;
  onEdit: () => void;
  last: boolean;
}) {
  const isLion = baby.name.toLowerCase() === 'lion';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        borderBottom: last ? 'none' : '1px solid var(--divider)',
      }}
    >
      <button
        type="button"
        onClick={onActivate}
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 16px',
          textAlign: 'left',
        }}
      >
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: 999,
            background: baby.color ?? 'var(--accent)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontFamily: 'Newsreader, serif',
            fontSize: 19,
          }}
        >
          {isLion ? <LionMark size={26} color="#fff" /> : baby.name[0]?.toUpperCase()}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 500 }}>{baby.name}</div>
          <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
            born {fmtDate(baby.birth_date)} · {ageLabel(baby.birth_date, { short: true })}
          </div>
        </div>
        {active && (
          <span
            className="lt-pill"
            style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
          >
            <span style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--accent)' }} />
            active
          </span>
        )}
      </button>
      <button
        type="button"
        aria-label={`Edit ${baby.name}`}
        onClick={onEdit}
        style={{
          padding: '12px 14px 12px 8px',
          color: 'var(--text-3)',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Icon.edit s={16} />
      </button>
    </div>
  );
}

function BabyFormSheet({ existing, onClose }: { existing?: Baby; onClose: () => void }) {
  const qc = useQueryClient();
  const isEdit = !!existing;
  const [name, setName] = useState(existing?.name ?? '');
  const [birth, setBirth] = useState(existing?.birth_date ?? new Date().toISOString().slice(0, 10));
  // Reconstruct a due-date display value from the stored ga_days so editing
  // round-trips cleanly: due = birth + (280 - ga_days) days.
  const initialDue =
    existing?.ga_days != null
      ? toIsoDate(isoToUtcMs(existing.birth_date) + (280 - existing.ga_days) * 86_400_000)
      : '';
  const [due, setDue] = useState(initialDue);
  const [color, setColor] = useState(existing?.color ?? BABY_COLORS[1]);
  const [sex, setSex] = useState<'M' | 'F' | null>(existing?.sex ?? null);

  // GA = 280 - (due − birth) in days, since due is by convention 40w (280d).
  // Both inputs are YYYY-MM-DD; we treat them as UTC midnights to dodge DST.
  const gaDays = (() => {
    if (!due || !birth) return null;
    const b = isoToUtcMs(birth) / 86_400_000;
    const d = isoToUtcMs(due) / 86_400_000;
    const ga = 280 - Math.round(d - b);
    return ga >= 140 && ga <= 315 ? ga : null;
  })();

  const save = useMutation({
    mutationFn: () => {
      const payload = { name: name.trim(), birth_date: birth, color, sex, ga_days: gaDays };
      return isEdit
        ? api(`/api/babies/${existing!.id}`, { method: 'PATCH', json: payload })
        : api('/api/babies', { method: 'POST', json: payload });
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Saved' : 'Baby added');
      qc.invalidateQueries({ queryKey: ['me'] });
      onClose();
    },
    onError: () => toast.error(isEdit ? "Couldn't save" : "Couldn't add baby"),
  });

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20,14,10,0.45)',
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        animation: 'lt-fade-in .15s ease',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-app)',
          borderRadius: '28px 28px 0 0',
          padding: '14px 18px 30px',
          maxWidth: 440,
          margin: '0 auto',
          width: '100%',
          animation: 'lt-sheet-in .2s ease',
        }}
      >
        <div style={{ width: 38, height: 4, borderRadius: 2, background: 'var(--text-3)', opacity: 0.5, margin: '0 auto 14px' }} />
        <h2 className="lt-serif" style={{ fontSize: 22, fontWeight: 500, margin: '0 0 14px' }}>
          {isEdit ? `Edit ${existing!.name}` : 'Add a baby'}
        </h2>
        <Field label="Name">
          <input
            type="text"
            value={name}
            autoFocus
            onChange={(e) => setName(e.target.value)}
            placeholder="Lion"
            style={{ width: '100%', fontSize: 17 }}
          />
        </Field>
        <Field label="Born">
          <input
            type="date"
            value={birth}
            onChange={(e) => setBirth(e.target.value)}
            style={{ width: '100%', fontSize: 17 }}
          />
        </Field>
        <Field label="Due date (optional, for growth advice)">
          <input
            type="date"
            value={due}
            onChange={(e) => setDue(e.target.value)}
            style={{ width: '100%', fontSize: 17 }}
          />
          {gaDays != null && (
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4 }}>
              Gestational age at birth: {Math.floor(gaDays / 7)}w
              {gaDays % 7 ? `+${gaDays % 7}d` : ''}
              {gaDays < 259 && (
                <span style={{ color: 'var(--warn)', marginLeft: 6 }}>· preterm</span>
              )}
            </div>
          )}
        </Field>
        <Field label="Accent color">
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            {BABY_COLORS.map((c) => (
              <button
                type="button"
                key={c}
                onClick={() => setColor(c)}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 999,
                  background: c,
                  border: c === color ? '3px solid var(--text-1)' : '3px solid transparent',
                  outline: 'none',
                }}
                aria-label={`color ${c}`}
              />
            ))}
          </div>
        </Field>
        <Field label="Sex (used by growth curves)">
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            {(['M', 'F'] as const).map((s) => {
              const on = sex === s;
              return (
                <button
                  type="button"
                  key={s}
                  onClick={() => setSex(s)}
                  style={{
                    flex: 1,
                    padding: '10px 0',
                    borderRadius: 12,
                    background: on ? 'var(--accent-soft)' : 'var(--bg-sunken)',
                    border: on ? '1px solid var(--accent)' : '1px solid transparent',
                    color: on ? 'var(--accent)' : 'var(--text-2)',
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  {s === 'M' ? 'Boy' : 'Girl'}
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => setSex(null)}
              style={{
                flex: 1,
                padding: '10px 0',
                borderRadius: 12,
                background: sex === null ? 'var(--bg-card)' : 'var(--bg-sunken)',
                border: sex === null ? '1px solid var(--border-strong)' : '1px solid transparent',
                color: 'var(--text-2)',
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              Skip
            </button>
          </div>
        </Field>
        <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
          <button
            type="button"
            className="lt-btn lt-btn-soft"
            style={{ flex: '0 0 110px', padding: '13px 0' }}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="lt-btn lt-btn-primary"
            style={{ flex: 1, padding: '13px 0' }}
            disabled={!name.trim() || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? (isEdit ? 'Saving…' : 'Adding…') : isEdit ? 'Save' : 'Add baby'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: '12px 16px',
        borderRadius: 14,
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        marginBottom: 10,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--text-3)',
          textTransform: 'uppercase',
          letterSpacing: 0.04,
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function fmtDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
